import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ROUTES } from '@/config/routes';

const SupportLandingPage = lazy(() =>
  import('./components/SupportLandingPage').then((m) => ({ default: m.SupportLandingPage })),
);

const HelpCenterPage = lazy(() =>
  import('./components/HelpCenterPage').then((m) => ({ default: m.HelpCenterPage })),
);

const DocumentationPage = lazy(() =>
  import('./components/DocumentationPage').then((m) => ({ default: m.DocumentationPage })),
);

const DocumentViewerPage = lazy(() =>
  import('./components/DocumentViewerPage').then((m) => ({ default: m.DocumentViewerPage })),
);

const SupportCenterPage = lazy(() =>
  import('./components/SupportCenterPage').then((m) => ({ default: m.SupportCenterPage })),
);

const TicketDetailPage = lazy(() =>
  import('./components/TicketDetailPage').then((m) => ({ default: m.TicketDetailPage })),
);

const AdminSupportPage = lazy(() =>
  import('./components/AdminSupportPage').then((m) => ({ default: m.AdminSupportPage })),
);

const AdminHelpCenterPage = lazy(() =>
  import('./components/AdminHelpCenterPage').then((m) => ({ default: m.AdminHelpCenterPage })),
);

const AdminDocumentationPage = lazy(() =>
  import('./components/AdminDocumentationPage').then((m) => ({
    default: m.AdminDocumentationPage,
  })),
);

const DocumentFormPage = lazy(() =>
  import('./components/DocumentFormPage').then((m) => ({ default: m.DocumentFormPage })),
);

const AdminTicketsPage = lazy(() =>
  import('./components/AdminTicketsPage').then((m) => ({ default: m.AdminTicketsPage })),
);

const AdminTicketDetailPage = lazy(() =>
  import('./components/AdminTicketDetailPage').then((m) => ({
    default: m.AdminTicketDetailPage,
  })),
);

/**
 * The Support area for any signed-in person: the landing page and what it leads to.
 *
 * <p>Listed beside the team routes in `app/router`, outside the product guard: support belongs to the
 * account and not to a product, so nobody has to be assigned anything to ask for help. Documentation, the
 * Support Center list and its ticket page join here as they are ported.
 */
export const supportRoutes: RouteObject[] = [
  { path: ROUTES.support, element: <SupportLandingPage /> },
  { path: ROUTES.supportDocs, element: <DocumentationPage /> },
  { path: ROUTES.supportDoc, element: <DocumentViewerPage /> },
  { path: ROUTES.supportContact, element: <SupportCenterPage /> },
  { path: ROUTES.supportTicket, element: <TicketDetailPage /> },
  { path: ROUTES.supportHelp, element: <HelpCenterPage /> },
];

/**
 * Support & Documentation management, the platform operator's.
 *
 * <p>Exported apart from {@link supportRoutes} because the router nests it under `SuperAdminRoute`,
 * which guards the group: a screen added here inherits it by where it is put. Every endpoint behind
 * these screens is `@SuperAdminOnly` and refuses on its own.
 */
export const supportPlatformRoutes: RouteObject[] = [
  { path: ROUTES.supportAdmin, element: <AdminSupportPage /> },
  { path: ROUTES.supportAdminHelp, element: <AdminHelpCenterPage /> },
  { path: ROUTES.supportAdminTickets, element: <AdminTicketsPage /> },
  { path: ROUTES.supportAdminTicket, element: <AdminTicketDetailPage /> },
  { path: ROUTES.supportAdminDocs, element: <AdminDocumentationPage /> },
  { path: ROUTES.supportAdminDocNew, element: <DocumentFormPage /> },
  { path: ROUTES.supportAdminDocEdit, element: <DocumentFormPage /> },
];
